import React, { useEffect, useState, useSyncExternalStore } from "react"
import { closeOverlay, openOverlay, useOverlayDispatch, useOverlayState } from "../overlay"

import ReAuth from "../../components/forms/re-auth"
import { SDK } from "../../sdk"
import { TState } from "../../sdk/state"
import client from "../../utils/client"

const APIClientContext = React.createContext<{ client: SDK; state: TState }>({ client: client, state: {} })

const useAPIClient = () => {
    const context = React.useContext(APIClientContext)
    if (context === undefined) {
        throw new Error("useAPIClient must be used within a APIClientProvider")
    }

    return context
}

type APIClientProviderProps = {
    client: SDK
    children: React.ReactNode
}

const APIClientProvider = ({ client, children }: APIClientProviderProps) => {
    const subscribe = (callback: () => void) => {
        const unsubscribe = client.state.subscribe(callback)

        window.addEventListener("visibilitychange", callback)

        return () => {
            unsubscribe()

            window.removeEventListener("visibilitychange", callback)
        }
    }

    const state = useSyncExternalStore(subscribe, client.state.getSnapshot, client.state.getServerSnapshot)

    const overlayDispatch = useOverlayDispatch()
    const { showOverlay } = useOverlayState()
    const [promiseHandlers, setPromiseHandlers] = useState<
        [(value: void | PromiseLike<void>) => void, (reason?: any) => void] | undefined
    >()

    useEffect(() => {
        if (!client) return

        client.mount([
            {
                hookKey: "reAuth",
                hookFn: () =>
                    new Promise((resolve, reject) => {
                        setPromiseHandlers([resolve, reject])
                        openOverlay(
                            overlayDispatch,
                            <ReAuth
                                onSuccess={() => {
                                    setPromiseHandlers(undefined)
                                    resolve(undefined)
                                    closeOverlay(overlayDispatch)
                                }}
                            />,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            13
                        )
                    }),
            },
        ])

        return () => {
            client.unmount()
        }
    }, [client, overlayDispatch])

    useEffect(() => {
        if (showOverlay || !promiseHandlers) return

        const [, reject] = promiseHandlers
        reject(undefined)
    }, [showOverlay, promiseHandlers])

    return <APIClientContext.Provider value={{ client, state }}>{children}</APIClientContext.Provider>
}

export { APIClientProvider, useAPIClient }
